import React, { useState, useEffect, useRef } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Box } from "@mui/material";

import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-5.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-5.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import NextButton from "../common/next-button";
import { apiBaseUrl } from "../consts/api-url.const";

type BudgetDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  onBudgetChange?: (budget: string) => void;
  onStartDateChange?: (startDate: string) => void;
  onEndDateChange?: (endDate: string) => void;
  onScheduleChange?: (schedule: "always" | "scheduled") => void;
  onTimezoneChange?: (timezone: string) => void;
  onStartDateTimeChange?: (date: Dayjs | null, time: string) => void;
  onEndDateTimeChange?: (date: Dayjs | null, time: string) => void;
  onCurrencyChange?: (currency: string) => void;
  isEditMode?: boolean;
  initialBudget?: string;
  initialSchedule?: "always" | "scheduled";
  initialTimezone?: string;
  initialStartDate?: Dayjs | null;
  initialEndDate?: Dayjs | null;
  initialStartTime?: string;
  initialEndTime?: string;
  initialCurrency?: string; // Currency from API response (for edit mode)
  adAccountId?: string; // Ad account ID to fetch budget info
};

const BudgetDropdown: React.FC<BudgetDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  onBudgetChange,
  onStartDateChange,
  onEndDateChange,
  onScheduleChange,
  onTimezoneChange,
  onStartDateTimeChange,
  onEndDateTimeChange,
  onCurrencyChange,
  isEditMode = false,
  initialBudget,
  initialSchedule,
  initialTimezone,
  initialStartDate,
  initialEndDate,
  initialStartTime,
  initialEndTime,
  initialCurrency,
  adAccountId,
}) => {
  const isOpen = expandedId === id;

  // Track if update is from parent prop (to prevent infinite loop)
  const isUpdatingFromParent = useRef(false);
  const hasFetchedApiBudget = useRef(false); // Track if API budget has been fetched

  const [dailyBudget, setDailyBudget] = useState(initialBudget ?? "");
  const [minBudget, setMinBudget] = useState<number>(0); // Will be set from API
  const [currency, setCurrency] = useState<string>(initialCurrency || ""); // Use initialCurrency if provided

  const [schedule, setSchedule] = useState<"always" | "scheduled">(
    initialSchedule || "always",
  );

  const [timeZone, setTimeZone] = useState(
    initialTimezone || "Helsinki, Finland (GMT+2)",
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(
    initialStartDate || null,
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate || null);
  const [startTime, setStartTime] = useState(initialStartTime || "00:00");
  const [endTime, setEndTime] = useState(initialEndTime || "00:00");

  // Fetch budget info from API when adAccountId changes
  // This ensures minBudget and currency are always up-to-date from Meta API
  useEffect(() => {
    const fetchBudgetInfo = async () => {
      if (!adAccountId) return;

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/meta-builder/budget-info/${adAccountId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("metaAdsPublicToken")}`,
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          console.log("Budget info fetched:", data);

          // Update currency from API response
          if (data.currency) {
            console.log("Setting currency:", data.currency);
            setCurrency(data.currency);
            // Notify parent of currency change
            if (onCurrencyChange) {
              onCurrencyChange(data.currency);
            }
          }

          // Find the minimum budget from the response
          if (data.minimumBudgets && data.minimumBudgets.length > 0) {
            const minBudgetData = data.minimumBudgets[0];

            // Get min_daily_budget_high_freq (in cents, convert to dollars)
            const minDailyBudgetHighFreq =
              minBudgetData.min_daily_budget_high_freq;
            if (minDailyBudgetHighFreq) {
              // Convert from cents to dollars and format to 2 decimal places
              const minBudgetInDollars = (minDailyBudgetHighFreq / 100).toFixed(
                2,
              );
              const minBudgetValue = parseFloat(minBudgetInDollars);
              console.log("Setting minimum budget:", minBudgetInDollars);

              // Update the minimum budget state
              setMinBudget(minBudgetValue);

              // Mark that we've fetched API budget
              hasFetchedApiBudget.current = true;

              // Only set daily budget from API if it's not already set (new campaign or draft restore without budget)
              // Don't override if user has already entered a value or if editing existing campaign
              if (!dailyBudget || dailyBudget === "") {
                setDailyBudget(minBudgetInDollars);

                // Notify parent of the API budget change immediately
                if (onBudgetChange) {
                  onBudgetChange(minBudgetInDollars);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching budget info:", error);
      }
    };

    fetchBudgetInfo();
  }, [adAccountId]);

  useEffect(() => {
    if (initialSchedule !== undefined) {
      setSchedule(initialSchedule);
    }
  }, [initialSchedule]);

  useEffect(() => {
    if (initialTimezone !== undefined) {
      setTimeZone(initialTimezone);
    }
  }, [initialTimezone]);

  useEffect(() => {
    if (initialStartDate !== undefined) {
      setStartDate(initialStartDate);
    }
  }, [initialStartDate]);

  useEffect(() => {
    if (initialEndDate !== undefined) {
      setEndDate(initialEndDate);
    }
  }, [initialEndDate]);

  useEffect(() => {
    if (initialStartTime !== undefined) {
      setStartTime(initialStartTime);
    }
  }, [initialStartTime]);

  useEffect(() => {
    if (initialEndTime !== undefined) {
      setEndTime(initialEndTime);
    }
  }, [initialEndTime]);

  // Update currency when initialCurrency prop changes (for edit mode)
  useEffect(() => {
    if (initialCurrency !== undefined) {
      setCurrency(initialCurrency);
      // Notify parent of currency change
      if (onCurrencyChange) {
        onCurrencyChange(initialCurrency);
      }
    }
  }, [initialCurrency, onCurrencyChange]);

  useEffect(() => {
    // Only apply initialBudget if we haven't fetched from API yet AND we haven't already set a value
    // This ensures API value takes priority over default value
    // Also prevents resetting when user has already entered a value
    if (
      initialBudget !== undefined &&
      !hasFetchedApiBudget.current &&
      dailyBudget === ""
    ) {
      isUpdatingFromParent.current = true;
      setDailyBudget(initialBudget);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromParent.current = false;
      }, 0);
    }
  }, [initialBudget]);

  useEffect(() => {
    // Only notify parent if update is NOT from parent prop (user input)
    if (onBudgetChange && !isUpdatingFromParent.current) {
      onBudgetChange(dailyBudget);
    }
  }, [dailyBudget, onBudgetChange]);

  const timeZones = [
    "Helsinki, Finland (GMT+2)",
    "UTC (GMT+0)",
    "New York, USA (GMT-5)",
    "Los Angeles, USA (GMT-8)",
    "London, UK (GMT+1)",
  ];

  // Map display timezone names to IANA timezone format
  const timeZoneToIanaMap: Record<string, string> = {
    "Helsinki, Finland (GMT+2)": "Europe/Helsinki",
    "UTC (GMT+0)": "UTC",
    "New York, USA (GMT-5)": "America/New_York",
    "Los Angeles, USA (GMT-8)": "America/Los_Angeles",
    "London, UK (GMT+1)": "Europe/London",
  };

  // Get IANA timezone format from display name
  const getIanaTimezone = (displayTimezone: string): string => {
    return timeZoneToIanaMap[displayTimezone] || "UTC";
  };

  // Format date from Dayjs to DD/MM/YYYY
  const formatDate = (date: Dayjs | null): string => {
    if (!date) return "";
    return date.format("DD/MM/YYYY");
  };

  useEffect(() => {
    if (onScheduleChange) {
      onScheduleChange(schedule);
    }
  }, [schedule, onScheduleChange]);

  useEffect(() => {
    if (onTimezoneChange) {
      // Send IANA timezone format instead of display format
      onTimezoneChange(getIanaTimezone(timeZone));
    }
  }, [timeZone, onTimezoneChange]);

  useEffect(() => {
    if (onStartDateTimeChange) {
      onStartDateTimeChange(startDate, startTime);
    }
  }, [startDate, startTime, onStartDateTimeChange]);

  useEffect(() => {
    if (onEndDateTimeChange) {
      onEndDateTimeChange(endDate, endTime);
    }
  }, [endDate, endTime, onEndDateTimeChange]);

  useEffect(() => {
    if (onStartDateChange) {
      if (schedule === "always") {
        onStartDateChange("None (run when published)");
      } else if (schedule === "scheduled") {
        if (startDate) {
          onStartDateChange(formatDate(startDate));
        } else {
          onStartDateChange("None (run when published)");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, startDate, onStartDateChange]);

  useEffect(() => {
    if (onEndDateChange) {
      if (schedule === "always") {
        onEndDateChange("None (run until turned off manually)");
      } else if (schedule === "scheduled") {
        if (endDate) {
          onEndDateChange(formatDate(endDate));
        } else {
          onEndDateChange("None (run until turned off manually)");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, endDate, onEndDateChange]);

  return (
    <div className="w-full">
      <div
        className="relative rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden"
        style={{
          borderRadius: "20px",
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity"
          style={{ height: "88px", padding: "24px" }}
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-3 flex-1">
            <img
              src={isOpen ? dropOpenIcon : campaignStepOneIcon}
              alt="Step 8"
              className="w-8 h-8"
            />
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "20px",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Budget
            </span>
          </div>

          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform"
            style={{
              height: "9.5px",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="px-6 pb-6">
            {/* 🔥 ALIGNMENT FIX ONLY */}
            <div className="ml-[41px] flex flex-col gap-6">
              {/* Budget */}
              <div className="flex flex-col gap-3">
                <h3
                  className="[font-family:'Poppins',Helvetica]"
                  style={{
                    color: "#FFFFFF",
                    fontSize: "20px",
                    fontWeight: 500,
                  }}
                >
                  Choose your Daily Budget Amount
                </h3>

                <div
                  className="flex items-center gap-4 bg-white rounded-[12px] text-[#020202]"
                  style={{ minHeight: "64px", padding: "16px 18px" }}
                >
                  <span className="text-[14px] font-semibold text-[#8A8A8A]">
                    {currency}
                  </span>

                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={minBudget.toFixed(2)}
                    value={dailyBudget}
                    onChange={(e) => {
                      const value = e.target.value;

                      // allow numbers + decimal only
                      if (!/^\d*\.?\d*$/.test(value)) return;

                      setDailyBudget(value);
                    }}
                    onBlur={() => {
                      const num = parseFloat(dailyBudget);
                      if (isNaN(num) || num < minBudget) {
                        setDailyBudget(minBudget.toFixed(2));
                      } else {
                        setDailyBudget(num.toFixed(2));
                      }
                    }}
                    className="flex-1 bg-transparent outline-none text-[14px] font-semibold"
                  />
                </div>

                <p
                  className="[font-family:'Poppins',Helvetica]"
                  style={{
                    color: "#8FA5BF",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  Your budget needs to be {minBudget} {currency} or greater or
                  your ads may not deliver.
                </p>
              </div>

              {/* Schedule */}
              <div className="flex flex-col gap-3">
                <h4
                  className="[font-family:'Poppins',Helvetica]"
                  style={{
                    color: "#FFFFFF",
                    fontSize: "20px",
                    fontWeight: 500,
                  }}
                >
                  Select when this Ad will be running
                </h4>

                <div className="flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={() => !isEditMode && setSchedule("always")}
                    disabled={isEditMode}
                    className="flex items-center gap-3 text-white text-[16px]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      className={`inline-flex h-5 w-5 rounded-full border-2 ${
                        schedule === "always"
                          ? "border-[#58F2A9] bg-[#58F2A9]"
                          : "border-[#878DA199]"
                      }`}
                    />
                    Always Active
                  </button>

                  <button
                    type="button"
                    onClick={() => !isEditMode && setSchedule("scheduled")}
                    disabled={isEditMode}
                    className="flex items-center gap-3 text-white text-[16px]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      className={`inline-flex h-5 w-5 rounded-full border-2 ${
                        schedule === "scheduled"
                          ? "border-[#58F2A9] bg-[#58F2A9]"
                          : "border-[#878DA199]"
                      }`}
                    />
                    Scheduled
                  </button>
                </div>
              </div>

              {/* Scheduled section remains UNCHANGED */}
              {schedule === "scheduled" && (
                <div className="flex flex-col gap-6">
                  {/* your existing scheduled UI exactly as-is */}
                </div>
              )}

              {/* Schedule Section */}
              {schedule === "scheduled" && (
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <span className="text-white text-[16px] font-medium">
                      Time Zone
                    </span>

                    <div
                      className="
      relative w-[340px] h-[56px]
      rounded-full
      flex items-center
      bg-gradient-to-b from-[#878DA11A] to-[#878DA11A]
      px-6
      "
                      style={{
                        opacity: isEditMode ? 0.6 : 1,
                      }}
                    >
                      <select
                        value={timeZone}
                        onChange={(e) => setTimeZone(e.target.value)}
                        disabled={isEditMode}
                        className="
        w-full h-full
        bg-transparent
        text-white text-[16px]
        outline-none
        appearance-none
        pr-10
      "
                        style={{
                          cursor: isEditMode ? "not-allowed" : "pointer",
                        }}
                      >
                        {timeZones.map((tz) => (
                          <option key={tz} className="bg-[#0F131A] text-white">
                            {tz}
                          </option>
                        ))}
                      </select>

                      {/* Chevron Icon */}
                      <svg
                        className="absolute right-6 w-5 h-5 text-white pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Titles */}
                  <div className="flex items-center gap-6">
                    <span className="text-white text-[16px] font-medium w-[180px]">
                      Start Date & Time
                    </span>
                    <span className="text-white text-[16px] font-medium w-[180px]">
                      End Date & Time
                    </span>
                  </div>

                  {/* Date Row */}
                  <div className="flex items-center gap-6 mt-2">
                    {/* Start Date */}
                    <div className="flex flex-col gap-2">
                      <div
                        className="
        flex items-center justify-between
        bg-gradient-to-b from-[#878DA11A] to-[#878DA11A]
        rounded-[16px] w-[180px] h-[50px]
        px-5 text-white
                           mt-[-20px]


      "
                        style={{
                          opacity: isEditMode ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="date"
                          value={
                            startDate ? startDate.format("YYYY-MM-DD") : ""
                          }
                          onChange={(e) =>
                            setStartDate(
                              e.target.value ? dayjs(e.target.value) : null,
                            )
                          }
                          disabled={isEditMode}
                          className="bg-transparent outline-none text-white w-full
                           appearance-none [color-scheme:dark]"
                          style={{
                            opacity: isEditMode ? 0.6 : 1,
                            cursor: isEditMode ? "not-allowed" : "pointer",
                          }}
                        />
                      </div>
                    </div>

                    {/* End Date */}
                    <div className="flex flex-col gap-2">
                      <div
                        className="
        flex items-center justify-between
        bg-gradient-to-b from-[#878DA11A] to-[#878DA11A]
        rounded-[16px] w-[180px] h-[50px]
        px-5 text-white
                   mt-[-20px]

      "
                      >
                        <input
                          type="date"
                          value={endDate ? endDate.format("YYYY-MM-DD") : ""}
                          onChange={(e) =>
                            setEndDate(
                              e.target.value ? dayjs(e.target.value) : null,
                            )
                          }
                          disabled={false}
                          className="bg-transparent outline-none text-white w-full
                           appearance-none [color-scheme:dark]"
                          style={{
                            cursor: "pointer",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Time Row */}
                  <div className="flex items-center gap-6 mt-3">
                    {/* Start Time */}
                    <div className="flex flex-col gap-2">
                      <div
                        className="
        flex items-center justify-between
        bg-gradient-to-b from-[#878DA11A] to-[#878DA11A]
        rounded-[16px] w-[180px] h-[50px]
        px-5 text-white
                   mt-[-20px]

      "
                        style={{
                          opacity: isEditMode ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          disabled={isEditMode}
                          className="bg-transparent outline-none text-white w-full
                                 appearance-none [color-scheme:dark]"
                          style={{
                            opacity: isEditMode ? 0.6 : 1,
                            cursor: isEditMode ? "not-allowed" : "pointer",
                          }}
                        />
                      </div>
                    </div>

                    {/* End Time */}
                    <div className="flex flex-col gap-2">
                      <div
                        className="
        flex items-center justify-between
        bg-gradient-to-b from-[#878DA11A] to-[#878DA11A]
        rounded-[16px] w-[180px] h-[54px]
        px-5 text-white
                   mt-[-20px]

      "
                      >
                        <input
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="bg-transparent outline-none text-white w-full
        appearance-none [color-scheme:dark]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Next button */}
              <div className="flex w-full justify-end">
                <NextButton size="sm" onClick={() => onNext?.(id)} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetDropdown;
